import React, { useEffect, useState } from 'react';
import {
    Box, Flex, Heading, Text, Badge, Button, Grid, GridItem,
    Spinner, Alert, AlertIcon, AlertDescription, useColorModeValue,
    useToast, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
    Tooltip, HStack, Tag, TagLabel, TagLeftIcon, Input, Select, FormControl, FormLabel,
    CheckboxGroup, Checkbox, Progress, FormErrorMessage,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    MdAdd, MdCheckCircle, MdCancel, MdLink, MdRefresh, MdLocalShipping
} from 'react-icons/md';
import axiosClient from '../../../axios-client';
import { useStateContext } from '../../../context/ContextProvider';
import countriesData from '../../../assets/dataJson/countries.json';
import { AsyncPaginate } from 'react-select-async-paginate';

export default function ShippingAPIs() {
    const { t } = useTranslation();
    const { user, dataSubscriptionUser, getAbonnementClient, currentBusiness } = useStateContext();
    const toast = useToast();

    const [companies, setCompanies] = useState([]);
    const [connections, setConnections] = useState([]);        // business-scoped
    const [allConnections, setAllConnections] = useState([]);  // cross-business
    const [loading, setLoading] = useState(true);

    const [saving, setSaving] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editingConn, setEditingConn] = useState(null);
    const [pendingCompany, setPendingCompany] = useState(null);
    const [showErrors, setShowErrors] = useState(false);
    const [formData, setFormData] = useState({ api_key: '', api_secret: '', source_name: 'Ecolinke', send_trigger: 'manual', is_active: true, countries: [], send_all_products: true, allowed_products: [] });
    const [businessProducts, setBusinessProducts] = useState([]);

    const cardBg = useColorModeValue('white', 'gray.700');
    const borderC = useColorModeValue('gray.200', 'gray.600');
    const subText = useColorModeValue('gray.500', 'gray.400');
    const connectedBg = useColorModeValue('teal.50', 'teal.900');
    const hubBg = useColorModeValue('gray.50', 'gray.800');

    const inputBg = useColorModeValue("#ffffff", "#1a202c");
    const textColor = useColorModeValue("#1e293b", "#f7fafc");
    const borderColor = useColorModeValue("#e2e8f0", "#4a5568");
    const backgroundSelect = useColorModeValue("#f1f5f9", "#4a5568");
    const selectMenuBg = useColorModeValue("#ffffff", "#2d3748");

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            padding: "2px",
            borderRadius: 10,
            borderColor: state.isFocused ? "#319795" : borderColor,
            boxShadow: state.isFocused ? "0 0 0 1px #319795" : "none",
            "&:hover": { borderColor: "#319795" },
            fontSize: 14,
            background: inputBg,
            color: textColor,
        }),
        singleValue: (base) => ({
            ...base,
            color: textColor,
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: "#319795",
            color: "#ffffff",
            borderRadius: "6px",
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: "#ffffff",
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: "#ffffff",
            "&:hover": {
                backgroundColor: "#e53e3e",
                color: "#ffffff",
            },
        }),
        menu: (base) => ({
            ...base,
            background: selectMenuBg,
            zIndex: 9999,
        }),
        option: (base, state) => ({
            ...base,
            fontSize: 14,
            backgroundColor: state.isSelected ? "#319795" : state.isFocused ? backgroundSelect : "transparent",
            color: state.isSelected ? "#ffffff" : textColor,
            "&:active": { backgroundColor: "#319795", color: "#ffffff" },
        }),
    };

    const loadProductOptions = async (search, loadedOptions, { page }) => {
        if (!user?.id || !currentBusiness?.id) return { options: [], hasMore: false };
        try {
            const res = await axiosClient.get(`/products/get_All_Products_names/?client=${user.id}&business=${currentBusiness.id}&page_size=20&page=${page}&search_term=${search}`, { hideLoader: true });
            const results = res.data.results || [];
            const hasMore = res.data.next !== null && res.data.next !== undefined;

            return {
                options: results.map(product => ({
                    value: product.id,
                    label: product.title
                })),
                hasMore: hasMore,
                additional: {
                    page: page + 1,
                },
            };
        } catch (error) {
            console.error('Error fetching products', error);
            return { options: [], hasMore: false };
        }
    };

    // Subscription limits
    // IMPORTANT: 0 = feature BLOCKED on this plan (not unlimited).
    //            N > 0 = at most N active connections allowed.
    const maxShipping = dataSubscriptionUser?.max_shipping_companies ?? 0;
    const hasSubscription = !!dataSubscriptionUser?.has_subscription;
    const shippingBlocked = hasSubscription && maxShipping === 0;
    // Count active connections across ALL businesses (subscription-level limit)
    const activeShipping = allConnections.filter(c => c.is_active).length;
    const isLimitReached = maxShipping > 0 && activeShipping >= maxShipping;

    // Fetch catalogue + connections
    const fetchAll = async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const [typesRes, connsRes, allConnsRes] = await Promise.all([
                axiosClient.get('/shipping-companies/'),
                axiosClient.get(`/shipping-connections/?user_id=${user.id}&business=${currentBusiness?.id}`),
                axiosClient.get(`/shipping-connections/?user_id=${user.id}`),
            ]);
            setCompanies(typesRes.data?.results || typesRes.data || []);
            setConnections(connsRes.data?.results || connsRes.data || []);
            setAllConnections(allConnsRes.data?.results || allConnsRes.data || []);

            // Also fetch products for the business
            if (currentBusiness?.id) {
                try {
                    const prodRes = await axiosClient.get(`/products/get_All_Products_names/?client=${user.id}&business=${currentBusiness.id}`);
                    setBusinessProducts(prodRes.data || []);
                } catch (errProd) {
                    console.error("Error fetching products", errProd);
                }
            }
        } catch (err) {
            // // console.error(err);
            toast({
                description: t('Erreur lors du chargement des APIs de livraison.'),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
        setLoading(false);
    };

    useEffect(() => { fetchAll(); }, [user?.id, currentBusiness?.id]);

    const getConnection = (companyId) =>
        connections.find((c) => c.shipping_company === companyId) || null;

    // Open Config Modal (for Connect or Edit)
    const openConfigModal = (company, existingConn = null) => {
        // If creating new and limit reached, block immediately
        if (!existingConn && isLimitReached) {
            toast({
                title: t('Limite atteinte'),
                description: t(`Vous avez atteint le maximum de connexions de livraison (${maxShipping}) autorisées par votre abonnement.`),
                status: 'warning',
                duration: 5000,
                isClosable: true,
                position: 'bottom-right',
            });
            return;
        }
        setPendingCompany(company);
        setEditingConn(existingConn);
        if (existingConn) {
            setFormData({
                api_key: existingConn.api_key || '',
                api_secret: existingConn.api_secret || '',
                source_name: existingConn.source_name || 'Ecolinke',
                send_trigger: existingConn.send_trigger || 'manual',
                is_active: existingConn.is_active,
                countries: Array.isArray(existingConn.countries) ? existingConn.countries : [],
                send_all_products: existingConn.send_all_products !== undefined ? existingConn.send_all_products : true,
                allowed_products: Array.isArray(existingConn.allowed_products) ? existingConn.allowed_products : []
            });
        } else {
            setFormData({
                api_key: '',
                api_secret: '',
                source_name: 'Ecolinke',
                send_trigger: 'manual',
                is_active: true,
                countries: [],
                send_all_products: true,
                allowed_products: []
            });
        }
        setShowErrors(false);
        setSaving(false);
        onOpen();
    };

    // Save Configuration
    const handleSaveConnection = async () => {
        // 0. Manual validation before saving
        const isCountryRequired = pendingCompany?.countries?.length > 0;
        const isProductRequired = !formData.send_all_products;
        const compCode = (pendingCompany?.code || '').toLowerCase();
        // CODNETWORK only needs api_key (Bearer token), not api_secret or source_name
        const needsApiSecret = compCode !== 'codnetwork';
        const needsSourceName = compCode !== 'codnetwork';
        if (
            !formData.api_key ||
            (needsApiSecret && !formData.api_secret) ||
            (needsSourceName && !formData.source_name) ||
            (isCountryRequired && formData.countries.length === 0) ||
            (isProductRequired && (!formData.allowed_products || formData.allowed_products.length === 0))
        ) {
            setShowErrors(true);
            toast({
                description: t("Veuillez remplir tous les champs obligatoires."),
                status: 'error',
                duration: 3000,
                position: 'bottom-right'
            });
            return;
        }

        setSaving(true);
        setShowErrors(false);

        // If activating and limit reached (on a new connection, or toggling existing one to active)
        const wasInactive = editingConn ? !editingConn.is_active : true;
        if (formData.is_active && wasInactive && isLimitReached) {
            // Exclude the current connection from active count when editing
            const otherActiveCount = allConnections.filter(c => c.is_active && c.id !== editingConn?.id).length;
            if (maxShipping > 0 && otherActiveCount >= maxShipping) {
                toast({
                    title: t('Limite atteinte'),
                    description: t(`Vous avez atteint le maximum de connexions actives (${maxShipping}). Désactivez-en une autre d'abord.`),
                    status: 'warning',
                    duration: 5000,
                });
                return;
            }
        }
        setSaving(true);
        try {
            if (editingConn) {
                // Update
                await axiosClient.patch(`/shipping-connections/${editingConn.id}/?user_id=${user.id}`, {
                    shipping_company: pendingCompany.id,
                    business: currentBusiness.id,
                    ...formData
                });
                toast({
                    description: t('Connexion modifiée avec succès.'),
                    status: 'success',
                    duration: 3000,
                });
            } else {
                // Create
                await axiosClient.post('/shipping-connections/', {
                    user_id: user.id,
                    shipping_company: pendingCompany.id,
                    business: currentBusiness.id,
                    ...formData
                });
                toast({
                    description: t('Connexion créée avec succès !'),
                    status: 'success',
                    duration: 3000,
                });
            }
            fetchAll();
            getAbonnementClient();
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.error || t("Erreur lors de l'enregistrement.");
            toast({
                description: msg,
                status: 'error',
                duration: 4000,
            });
        }
        setSaving(false);
    };

    // Delete Connection
    const handleDeleteConnection = async () => {
        if (!editingConn) return;
        setSaving(true);
        try {
            await axiosClient.delete(`/shipping-connections/${editingConn.id}/?user_id=${user.id}`);
            toast({
                description: t('Connexion supprimée.'),
                status: 'info',
                duration: 3000,
            });
            fetchAll();
            getAbonnementClient();
            onClose();
        } catch (err) {
            toast({ description: t('Erreur.'), status: 'error', duration: 3000 });
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <Flex justify="center" align="center" minH="300px">
                <Spinner size="xl" color="teal.400" />
            </Flex>
        );
    }

    return (
        <Box bg={hubBg} minH="100vh" p={{ base: 4, md: 8 }}>
            <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} mb={8} gap={4}>
                <Box>
                    <Heading size="lg" mb={1}>
                        🚚 {t('Plateformes de Livraison')}
                    </Heading>
                    <Text color={subText} fontSize="sm">
                        {t('Connectez vos plateformes de livraison pour expédier automatiquement vos commandes.')}
                    </Text>
                </Box>
                <HStack>
                    <Tag colorScheme="teal" size="lg" rounded="full" px={4}>
                        <TagLeftIcon as={MdLocalShipping} />
                        <TagLabel>{activeShipping} {t('connectée(s)')}</TagLabel>
                    </Tag>
                    <Button size="sm" leftIcon={<MdRefresh />} onClick={fetchAll} variant="outline">
                        {t('Actualiser')}
                    </Button>
                </HStack>
            </Flex>

            {/* ── Subscription limit bar ───────────────────────────────── */}
            {shippingBlocked && (
                <Alert status="error" mb={6} rounded="xl" p={5}>
                    <AlertIcon boxSize={6} />
                    <Box ml={2}>
                        <Text fontWeight="bold" mb={1}>
                            {t('Fonctionnalit\u00e9 non disponible sur votre plan actuel')}
                        </Text>
                        <AlertDescription fontSize="sm">
                            {t('Votre plan ne permet pas les connexions aux soci\u00e9t\u00e9s de livraison. Passez \u00e0 un plan sup\u00e9rieur.')}
                        </AlertDescription>
                    </Box>
                </Alert>
            )}

            {!shippingBlocked && maxShipping > 0 && (
                <Box mb={6} p={4} bg={cardBg} rounded="xl" shadow="sm" borderWidth="1px" borderColor={borderC}>
                    <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm" fontWeight="semibold">{t('Connexions de livraison actives')}</Text>
                        <Text fontSize="sm" color={isLimitReached ? 'red.500' : subText} fontWeight="bold">
                            {activeShipping} / {maxShipping}
                        </Text>
                    </Flex>
                    <Progress
                        value={(activeShipping / maxShipping) * 100}
                        colorScheme={isLimitReached ? 'red' : activeShipping / maxShipping >= 0.8 ? 'orange' : 'teal'}
                        rounded="full"
                        size="sm"
                    />
                    {isLimitReached && (
                        <Alert status="warning" mt={3} rounded="lg" py={2}>
                            <AlertIcon />
                            <AlertDescription fontSize="sm">
                                {t(`Limite atteinte. Désactivez une connexion existante pour en activer une nouvelle.`)}
                            </AlertDescription>
                        </Alert>
                    )}
                </Box>
            )}

            <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
                {companies.map((company) => {
                    const conn = getConnection(company.id);
                    const isConnected = !!conn;
                    const isActive = conn?.is_active;
                    // Card blocked when: plan forbids shipping (0) OR limit is reached for inactive/new cards
                    const wouldExceedLimit = shippingBlocked || (isLimitReached && (!isConnected || !isActive));

                    return (
                        <GridItem key={company.id}>
                            <Box
                                bg={isConnected ? connectedBg : cardBg}
                                border="2px solid"
                                borderColor={isConnected ? `teal.400` : borderC}
                                rounded="2xl"
                                p={6}
                                shadow={isConnected ? 'md' : 'sm'}
                                transition="all 0.2s"
                                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                                position="relative"
                                opacity={wouldExceedLimit ? 0.8 : 1}
                            >
                                {isConnected && (
                                    <Badge position="absolute" top={3} right={3} colorScheme={isActive ? 'green' : 'red'} rounded="full" px={2}>
                                        {isActive ? t('Actif') : t('Inactif')}
                                    </Badge>
                                )}

                                <Flex align="center" gap={3} mb={4}>
                                    <Box>
                                        <MdLocalShipping size={36} color="#38b2ac" />
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold" fontSize="lg">{company.name}</Text>
                                        <Badge colorScheme="teal" fontSize="xs">{company.code}</Badge>
                                    </Box>
                                </Flex>

                                <Text fontSize="sm" color={subText} mb={5} noOfLines={3}>
                                    {company.description || t('Plateforme de livraison.')}
                                </Text>

                                {isConnected && (
                                    <Flex gap={4} mb={4} fontSize="xs" color={subText}>
                                        <Box>
                                            <Text fontWeight="semibold">{conn.orders_sent}</Text>
                                            <Text>{t('envois')}</Text>
                                        </Box>
                                        <Box>
                                            <Text fontWeight="semibold" color={"teal.600"}>
                                                {conn.send_trigger === 'on_create' && t('On Create')}
                                                {conn.send_trigger === 'on_confirmed' && t('On Confirmed')}
                                                {conn.send_trigger === 'on_packed' && t('On Packed')}
                                                {conn.send_trigger === 'manual' && t('Manual')}
                                            </Text>
                                            <Text>{t('Trigger')}</Text>
                                        </Box>
                                    </Flex>
                                )}

                                <Flex gap={2} flexWrap="wrap">
                                    {!isConnected ? (
                                        <Tooltip
                                            label={
                                                shippingBlocked
                                                    ? t('Votre plan ne permet pas les connexions de livraison')
                                                    : isLimitReached
                                                        ? t(`Limite de ${maxShipping} connexions de livraison atteinte`)
                                                        : ''
                                            }
                                            isDisabled={!shippingBlocked && !isLimitReached}
                                        >
                                            <Button
                                                size="sm"
                                                colorScheme="teal"
                                                leftIcon={<MdAdd />}
                                                onClick={() => openConfigModal(company)}
                                                w="full"
                                                isDisabled={shippingBlocked || isLimitReached}
                                                opacity={shippingBlocked || isLimitReached ? 0.5 : 1}
                                            >
                                                {t('Configurer')}
                                            </Button>
                                        </Tooltip>
                                    ) : (
                                        <Button
                                            size="sm"
                                            colorScheme="teal"
                                            variant="outline"
                                            onClick={() => openConfigModal(company, conn)}
                                            w="full"
                                        >
                                            {t('Paramètres')}
                                        </Button>
                                    )}
                                </Flex>
                            </Box>
                        </GridItem>
                    );
                })}
            </Grid>

            {/* Modal */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{t('Configurer')} {pendingCompany?.name}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl mb={4} isRequired isInvalid={!formData.api_key && showErrors}>
                            <FormLabel>{(pendingCompany?.code || '').toLowerCase() === 'codnetwork' ? t('Token API (Bearer Token)') : t('Clé API (API Key)')}</FormLabel>
                            <Input
                                placeholder={(pendingCompany?.code || '').toLowerCase() === 'codnetwork' ? 'ex. tk_xxxxx' : 'ex. sk_live_xxx'}
                                value={formData.api_key}
                                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                            />
                            {!formData.api_key && showErrors && <FormErrorMessage>{t('Ce champ est obligatoire.')}</FormErrorMessage>}
                        </FormControl>
                        {(pendingCompany?.code || '').toLowerCase() !== 'codnetwork' && (
                        <FormControl mb={4} isRequired isInvalid={!formData.api_secret && showErrors}>
                            <FormLabel>{t('Secret API (API Secret)')}</FormLabel>
                            <Input
                                placeholder="ex. sec_xxx"
                                value={formData.api_secret}
                                onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                            />
                            {!formData.api_secret && showErrors && <FormErrorMessage>{t('Ce champ est obligatoire.')}</FormErrorMessage>}
                        </FormControl>
                        )}
                        {(pendingCompany?.code || '').toLowerCase() !== 'codnetwork' && (
                        <FormControl mb={4} isRequired isInvalid={!formData.source_name && showErrors}>
                            <FormLabel>{t('Source Name')}</FormLabel>
                            <Input
                                placeholder="ex. Ecolinke"
                                value={formData.source_name}
                                onChange={(e) => setFormData({ ...formData, source_name: e.target.value })}
                            />
                            {!formData.source_name && showErrors && <FormErrorMessage>{t('Ce champ est obligatoire.')}</FormErrorMessage>}
                        </FormControl>
                        )}

                        {pendingCompany?.countries?.length > 0 && (
                            <FormControl mb={4} isRequired isInvalid={formData.countries.length === 0 && showErrors}>
                                <FormLabel>{t('Pays Autorisés')}</FormLabel>
                                <CheckboxGroup
                                    colorScheme="teal"
                                    value={formData.countries}
                                    onChange={(vals) => setFormData({ ...formData, countries: vals })}
                                >
                                    <HStack spacing={4} flexWrap="wrap">
                                        {pendingCompany.countries.map(code => {
                                            const country = countriesData.countries.find(c => c.value === code);
                                            return (
                                                <Checkbox key={code} value={code}>
                                                    {country ? country.label : code}
                                                </Checkbox>
                                            );
                                        })}
                                    </HStack>
                                </CheckboxGroup>
                                {formData.countries.length === 0 && showErrors && <FormErrorMessage>{t('Veuillez sélectionner au moins un pays.')}</FormErrorMessage>}
                            </FormControl>
                        )}
                        <FormControl mb={4} isRequired>
                            <FormLabel>{t("Déclencheur d'envoi")}</FormLabel>
                            <Select
                                value={formData.send_trigger}
                                onChange={(e) => setFormData({ ...formData, send_trigger: e.target.value })}
                            >
                                <option value="on_create">{t("Lors de la création de la commande (on_create)")}</option>
                                <option value="on_confirmed">{t("Lors de la confirmation (on_confirmed)")}</option>
                                <option value="on_packed">{t("Lors de l'emballage (on_packed)")}</option>
                                <option value="manual">{t("Manuellement (manual)")}</option>
                            </Select>
                        </FormControl>

                        {/* Configurer les produits autorisés */}
                        <FormControl mb={4}>
                            <Checkbox
                                colorScheme="teal"
                                isChecked={formData.send_all_products}
                                onChange={(e) => setFormData({ ...formData, send_all_products: e.target.checked })}
                            >
                                {t("Envoyer tous les produits")}
                            </Checkbox>
                            <Text fontSize="xs" color={subText} mt={1}>
                                {t("Si coché, toutes les commandes contenant n'importe quel produit seront envoyées à ce transporteur.")}
                            </Text>
                        </FormControl>

                        {!formData.send_all_products && (
                            <FormControl mb={4} isRequired isInvalid={(!formData.allowed_products || formData.allowed_products.length === 0) && showErrors}>
                                <FormLabel>{t("Produits Autorisés")}</FormLabel>
                                <AsyncPaginate
                                    isMulti
                                    additional={{ page: 1 }}
                                    loadOptions={loadProductOptions}
                                    value={businessProducts
                                        .filter(p => formData.allowed_products.includes(p.id))
                                        .map(p => ({ value: p.id, label: p.title }))}
                                    onChange={(selected) => setFormData({
                                        ...formData,
                                        allowed_products: selected ? selected.map(s => s.value) : []
                                    })}
                                    placeholder={t("Sélectionner les produits...")}
                                    styles={selectStyles}
                                    noOptionsMessage={() => t("Aucun produit trouvé")}
                                />
                                {(!formData.allowed_products || formData.allowed_products.length === 0) && showErrors && (
                                    <FormErrorMessage>{t("Veuillez sélectionner au moins un produit.")}</FormErrorMessage>
                                )}
                            </FormControl>
                        )}

                        <FormControl mb={4}>
                            <FormLabel>{t('État')}</FormLabel>
                            <Select
                                value={formData.is_active ? 'active' : 'inactive'}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                                isDisabled={shippingBlocked}
                            >
                                <option value="active" disabled={
                                    /* disable Actif when limit reached and current conn is inactive or new */
                                    isLimitReached && (editingConn ? !editingConn.is_active : true) &&
                                    allConnections.filter(c => c.is_active && c.id !== editingConn?.id).length >= maxShipping
                                }>
                                    {t('Actif')}
                                </option>
                                <option value="inactive">{t('Inactif')}</option>
                            </Select>
                            {isLimitReached && formData.is_active && editingConn && !editingConn.is_active && (
                                <Text fontSize="xs" color="orange.500" mt={1}>
                                    ⚠️ {t(`Limite de ${maxShipping} connexions actives atteinte. Désactivez-en une autre d'abord.`)}
                                </Text>
                            )}
                        </FormControl>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        {editingConn && (
                            <Button colorScheme="red" variant="outline" onClick={handleDeleteConnection} isLoading={saving} mr="auto">
                                {t('Supprimer')}
                            </Button>
                        )}
                        <Button colorScheme="teal" onClick={handleSaveConnection} isLoading={saving}>
                            {t('Enregistrer')}
                        </Button>
                        <Button variant="ghost" onClick={onClose} isDisabled={saving}>
                            {t('Annuler')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
